import {
  type FC, useCallback, useMemo,
} from 'react';
import { useDispatch } from 'react-redux';
import { useGeolocationInputProps } from '~/_shared/components/textInputWithGeolocation/useGeolocationInputProps';
import {
  isResourceLoading, isResourcePartiallyLoaded,
} from '~/_shared/types/fetchableResource/fetchableResource.helpers';
import { createUuid } from '~/_shared/utils/createUuid';
import type { GeoLocation } from '~/_shared/utils/geolocation/geolocation';
import { useTranslation } from '~/_shared/utils/hooks';
import { notNullsy } from '~/_shared/utils/typeGuards';
import { PlacesSearchResultsRankingPreference } from '~/places/places.repository';
import type { Place } from '~/places/places.types';
import { usePlacesHelpers } from '~/places/usePlacesHelpers';
import { SelectLocationConfiguration } from '~/store/frontendState/mapTools/selectLocation/selectLocation.state';
import {
  placesSearchesRequestNextPage, placesToolSetStartingLocation,
} from '~/store/frontendState/places/places.actionCreators';
import {
  usePlacesSearchResultsSelector, usePlacesStartingLocationSelector,
} from '~/store/frontendState/places/places.selectors';
import {
  mapSettingsPlacesAddSearch, mapSettingsPlacesRemoveSearch,
} from '~/store/mapSettings/places/mapSettingsPlaces.actionCreators';
import { useMapSettingsPlacesSearchesSelector } from '~/store/mapSettings/places/mapSettingsPlaces.selectors';
import type {
  PlacesDistance, PlacesSearchId,
} from '~/store/mapSettings/places/mapSettingsPlaces.state';
import {
  toolStateHidePlaceSearch, toolStateShowPlaceSearch,
} from '~/store/mapSettings/toolsState/visibility/visibility.actionCreators';
import { useHiddenPlaceSearchIdsSelector } from '~/store/mapSettings/toolsState/visibility/visibility.selectors';
import { useToolCollapseState } from '../useToolCollapseState';
import { usePlacesErrors } from './hooks/usePlacesErrors';
import {
  type PlacesSearchProps, PlacesToolComponent,
} from './placesTool.component';
import {
  getNextPlacesSearchColor, getPlaceSearchName,
} from './placesTool.helpers';

export type PlaceSearchListingItem = Readonly<{
  id: PlacesSearchId;
  isLoading: boolean;
  displayName: string;
  text: string;
  center: string;
  distance: PlacesDistance;
  color: string;
  items: ReadonlyArray<Place>;
  isVisible: boolean;
  onLoadMore?: () => void;
  onRemove: () => void;
  onZoomToArea: () => void;
  onVisibilityToggle?: () => void;
}>;

export const PlacesToolContainer: FC = () => {
  const { isToolCollapsed } = useToolCollapseState();
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const placesSearches = useMapSettingsPlacesSearchesSelector();
  const places = usePlacesSearchResultsSelector();
  const startingLocationState = usePlacesStartingLocationSelector();
  const hiddenPlaceSearchIds = useHiddenPlaceSearchIdsSelector();
  const { overLimitMessage, overPlacesLimit } = usePlacesErrors();
  const { zoomToPlacesSearchArea } = usePlacesHelpers();

  const error = overLimitMessage?.message || null;

  const setStartingLocationState = useCallback((startingLocation: GeoLocation) => {
    dispatch(placesToolSetStartingLocation(startingLocation));
  }, [dispatch]);

  const {
    address,
    wasAddressTyped,
    geolocationInputProps,
    getLatLngFromAddress,
  } = useGeolocationInputProps({
    configuration: SelectLocationConfiguration.SetPlacesToolStartLocation,
    startingLocationState,
    setStartingLocationState,
  });

  const createSearch = useCallback(async ({ searchField, distance }: PlacesSearchProps) => {
    const onSuccess = (location: GeoLocation) => {
      dispatch(mapSettingsPlacesAddSearch({
        newSearch: {
          id: createUuid(),
          preference: PlacesSearchResultsRankingPreference.Relevance,
          text: searchField,
          center: {
            name: location.address,
            latLng: location.latLng,
          },
          distance,
          color: getNextPlacesSearchColor(placesSearches.map(search => search.color)),
        },
        zoomTo: true,
      }));
    };

    if (wasAddressTyped) {
      onSuccess(await getLatLngFromAddress({ address }));
    }
    else if (startingLocationState) {
      onSuccess(startingLocationState);
    }
  }, [address, dispatch, getLatLngFromAddress, placesSearches, startingLocationState, wasAddressTyped]);

  const placeResults: ReadonlyArray<PlaceSearchListingItem> = useMemo(() => (
    placesSearches
      .map(placeSearch => {
        const { id, text, center: { name, latLng }, distance } = placeSearch;
        const lookup = places.get(id);

        const items = isResourcePartiallyLoaded(lookup) ? lookup.value.items : [];
        const isLoading = isResourceLoading(lookup);
        const isVisible = !hiddenPlaceSearchIds.includes(id);

        return {
          center: name || `lat: ${latLng.lat} | lng: ${latLng.lng}`,
          color: placeSearch.color,
          distance,
          displayName: getPlaceSearchName(placeSearch, t),
          id,
          items,
          isLoading,
          text,
          isVisible,
          onVisibilityToggle: () => {
            if (isVisible) {
              dispatch(toolStateHidePlaceSearch(id));
            }
            else {
              dispatch(toolStateShowPlaceSearch(id));
            }
          },
          onZoomToArea: () => {
            zoomToPlacesSearchArea(id);
          },
          onRemove: () => {
            dispatch(mapSettingsPlacesRemoveSearch(placeSearch));
          },
          onLoadMore: isResourcePartiallyLoaded(lookup) && lookup.value.hasMore && !overPlacesLimit ? () => {
            dispatch(placesSearchesRequestNextPage(new Set([id])));
          } : undefined,
        };
      })
      .filter(notNullsy)
  ), [dispatch, places, placesSearches, hiddenPlaceSearchIds, zoomToPlacesSearchArea, t, overPlacesLimit]);

  const isAddressDataValid = !error && (
    wasAddressTyped
      ? address.trim().length > 0
      : !!startingLocationState
  );

  return (
    <PlacesToolComponent
      error={error}
      isAddressDataValid={isAddressDataValid}
      isLegendMode={isToolCollapsed}
      createSearch={createSearch}
      isFormLoading={!!geolocationInputProps.isLoading}
      geolocationInputProps={geolocationInputProps}
      searches={placeResults}
    />
  );
};
